import { trackGAEvent } from '@bino/utils/functions/ga';
import { onReady } from './util/onReady';
import CrossSvg from '@bi-digital/icons/svg/cross.svg?raw';

interface FormEvent extends Event {
    type: 'elementValidated' | 'formsSetupCompleted' | 'formsSubmitted' | 'formsSubmittedError';
    isValid?: boolean;
    formData?: FormData;
}

interface EpiFormEventMap {
    formsStartSubmitting: FormEvent;
    formsSubmitted: FormEvent;
    elementValidated: FormEvent;
    formsSetupCompleted: FormEvent;
    formsSubmittedError: FormEvent;
}

interface EpiForm extends EventTarget {
    addEventListener<K extends keyof EpiFormEventMap>(
        type: K,
        listener: (this: EpiForm, ev: EpiFormEventMap[K]) => void,
        options?: boolean | AddEventListenerOptions,
    ): void;
    addEventListener(
        type: string,
        listener: EventListenerOrEventListenerObject,
        options?: boolean | AddEventListenerOptions,
    ): void;
}

declare global {
    interface Window {
        $$epiforms: typeof jQuery;
    }
}

function initValidationCustomElements(): void {
    document.querySelectorAll<HTMLElement>('.EPiServerForms .Form__Element').forEach((element) => {
        const validationSpan = element.querySelector('.Form__Element__ValidationError') as HTMLSpanElement;
        const formElement = element.querySelector('input.bi-input, textarea.bi-input, select.bi-input');
        if (!validationSpan || !formElement) return;
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type !== 'attributes' && mutation.attributeName !== 'class') return;
                const hasError = !validationSpan.classList.contains('hidden');
                formElement.setAttribute('aria-invalid', hasError.toString());
            });
        });
        observer.observe(validationSpan, { attributes: true, attributeFilter: ['class'] });
    });
}

function initFileUpload() {
    document.querySelectorAll<HTMLInputElement>(".EPiServerForms input[type='file']").forEach((element) => {
        element.style.display = 'none';
        const button = document.querySelector(`button.bi-button[name=${element.name}]`);
        const previewList = document.querySelector<HTMLElement>(`ul#${element.name}-items`);
        if (!button || !previewList) return;
        button.addEventListener('click', () => {
            element.click();
        });
        element.addEventListener('change', () => {
            previewList.childNodes.forEach((node) => node.remove());
            const elementFiles = element.files;
            if (!elementFiles) return;
            const files: string[] = [];
            const nodes = Array.from(elementFiles).map((file) => {
                const listItem = document.createElement('li');
                listItem.id = file.name;
                files.push(file.name);
                const fileName = document.createElement('span');
                fileName.textContent = file.name;
                const removeButton = document.createElement('button');
                removeButton.classList.add('bi-button');
                removeButton.setAttribute('data-icon', 'true');
                removeButton.setAttribute('data-variant', 'text');
                removeButton.setAttribute('data-size', 'small');
                removeButton.setAttribute('aria-label', 'Remove file');
                removeButton.innerHTML = CrossSvg;
                removeButton.addEventListener('click', () => {
                    element.value = '';
                    listItem.remove();
                });
                listItem.append(removeButton, fileName);

                return listItem;
            });
            if (files.length > 0) {
                previewList?.append(...nodes);
            }
        });
    });
}

function addFormVariables(formData: FormData | null): void {
    if (!formData) return;

    const query = window.location.search.match(/[?&]productid=(\d+)/i);
    if (!query?.[1]) return;

    formData.append('bi_productId', query[1]);
}

function wireUpGoogleAnalyticsEvents(formName: string): void {
    const forms = document.querySelector('.EPiServerForms');
    if (!forms) return;

    const handleFormEvent = (event: FormEvent) => {
        if ((event.type === 'elementValidated' && event.isValid === false) || event.type === 'formsSubmittedError') {
            trackGAEvent({ event: 'FormFailed', data: { FormName: formName } });
        }

        if (event.type === 'formsSubmitted') {
            trackGAEvent({ event: 'FormCompleted', data: { FormName: formName } });
        }
    };

    ['elementValidated', 'formsSetupCompleted', 'formsSubmitted', 'formsSubmittedError'].forEach((eventName) => {
        forms.addEventListener(eventName, handleFormEvent as EventListener);
    });
}

function scrollToTopAfterSubmit(): void {
    const forms = document.querySelector('.EPiServerForms');
    if (!forms) return;

    forms.addEventListener('formsSubmitted', () => {
        setTimeout(() => window.scrollTo(0, 0), 250);
    });
}

function onClassChange(element: HTMLElement): () => void {
    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            if (mutation.attributeName !== 'class') return;

            const target = mutation.target as HTMLElement;
            const attributeValue = target.className;
            const inputChild = element.querySelector<HTMLInputElement>(
                '.FormTextbox__Input, .FormChoice__Input--Radio, .FormChoice__Input--Checkbox',
            );

            if (!inputChild) return;

            const isInvalid = attributeValue.includes('ValidationFail');
            inputChild.setAttribute('aria-invalid', String(isInvalid));

            if (inputChild.classList.contains('FormChoice__Input--Radio')) {
                element.removeAttribute('aria-invalid');
            }
        });
    });

    observer.observe(element, { attributes: true });
    return () => observer.disconnect();
}

function addObserverToRequiredFields(): void {
    document.querySelectorAll<HTMLElement>('.Form__Element.ValidationRequired').forEach(onClassChange);
}

onReady(() => {
    if (typeof window.$$epiforms === 'undefined') return;
    initFileUpload();
    initValidationCustomElements();
    addObserverToRequiredFields();

    const forms = document.querySelector('.EPiServerForms') as EpiForm;
    if (!forms) return;

    forms.addEventListener('formsStartSubmitting', (event: FormEvent) => {
        if (event.formData) {
            addFormVariables(event.formData);
        }
    });

    const formName = document.querySelector<HTMLMetaElement>("meta[property='og:title']")?.content;
    if (!formName) return;

    trackGAEvent({ event: 'FormLoaded', data: { FormName: formName } });

    wireUpGoogleAnalyticsEvents(formName);
    scrollToTopAfterSubmit();
});
